import {
  Box,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  SeV2QuestionStatus,
  useGetBuyerAnnouncementsQuery,
  useGetBuyerSeV2QuestionsQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { BuyerQuestionInbox } from "./BuyerQuestionInbox";
import { InformationComposer } from "./InformationComposer";
import { PublicFeedPreview } from "./PublicFeedPreview";

export const CommunicationTab = ({ eventId }: { eventId: string }) => {
  const { t } = useTranslation();
  const displayer = useApiError();

  const {
    data: questionsData,
    error: questionsError,
    isLoading: questionsLoading,
  } = useGetBuyerSeV2QuestionsQuery({
    eventId,
  });
  const {
    data: announcementsData,
    error: announcementsError,
    isLoading: announcementsLoading,
  } = useGetBuyerAnnouncementsQuery({ eventId });

  useEffect(() => {
    if (questionsError) displayer.triggerSoft(questionsError);
  }, [questionsError, displayer]);
  useEffect(() => {
    if (announcementsError) displayer.triggerSoft(announcementsError);
  }, [announcementsError, displayer]);

  if (questionsLoading || announcementsLoading) {
    return (
      <Box py="10" textAlign="center">
        <Spinner color="smPrimary" />
      </Box>
    );
  }

  const questions = questionsData?.questions ?? [];
  const announcements = announcementsData?.announcements ?? [];
  const unanswered = questions.filter(
    (q) => q.status === SeV2QuestionStatus.Unanswered,
  ).length;

  // Split announcements by source: an announcement is a published Q&A if a question points to it; otherwise standalone.
  const publishedAnnouncementIds = new Set(
    questions
      .map((q) => q.publishedAnnouncementId)
      .filter((id): id is string => !!id),
  );
  const qaAnnouncements = announcements.filter((a) =>
    publishedAnnouncementIds.has(a.id),
  );
  const infoAnnouncements = announcements.filter(
    (a) => !publishedAnnouncementIds.has(a.id),
  );

  return (
    <Tabs colorScheme="teal" isLazy>
      <TabList>
        <Tab>
          {t("Questions")}
          {unanswered > 0 ? ` (${unanswered})` : ""}
        </Tab>
        <Tab>
          {t("Public Q&A")}
          {qaAnnouncements.length > 0 ? ` (${qaAnnouncements.length})` : ""}
        </Tab>
        <Tab>
          {t("Information to tenderers")}
          {infoAnnouncements.length > 0 ? ` (${infoAnnouncements.length})` : ""}
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel px="0">
          <BuyerQuestionInbox eventId={eventId} questions={questions} />
        </TabPanel>
        <TabPanel px="0">
          <PublicFeedPreview eventId={eventId} announcements={qaAnnouncements} />
        </TabPanel>
        <TabPanel px="0">
          <InformationComposer
            eventId={eventId}
            announcements={infoAnnouncements}
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
